/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { Toggle } from "@eds-web/react";
import { css } from "@panda/css";
import { HStack } from "@panda/jsx";
import { useCallback } from "react";
import type { FilterOption } from "../core/Directory.types";

const rowStyles = css({
	flexWrap: "wrap",
	gap: "eds.dimension.8",
});

export type DirectoryChipRowProps = {
	options: FilterOption[];
	values: string[];
	onToggle: (value: string) => void;
	/** Accessible name for the `role="group"` container (WCAG 4.1.2). Required. */
	label: string;
};

/**
 * Multi-value chip row. Each chip independently toggles on/off. Semantics in the
 * filter engine (OR/AND) are controlled by the `matchMode` prop on the FilterDef,
 * not by this UI component.
 *
 * A11y: the row is a `role="group"` with an accessible name; each chip surfaces
 * `aria-pressed` via EDS Toggle.
 */
export function DirectoryChipRow({ options, values, onToggle, label }: DirectoryChipRowProps): React.JSX.Element {
	if (process.env.NODE_ENV !== "production" && !label.trim()) {
		console.warn(
			"[DirectoryChipRow] An empty `label` was passed; the rendered group will have no accessible name. Supply a meaningful label at the FilterDef.",
		);
	}

	const handleChange = useCallback(
		(value: string) => {
			onToggle(value);
		},
		[onToggle],
	);

	return (
		<HStack role="group" aria-label={label} className={rowStyles}>
			{options.map((option) => {
				const selected = values.includes(option.value);
				return (
					<Toggle
						key={option.value}
						label={option.label}
						selected={selected}
						disabled={option.disabled && !selected}
						onChange={() => handleChange(option.value)}
						size="md"
						shape="round"
						value={option.value}
					/>
				);
			})}
		</HStack>
	);
}
